import {BrowserModule} from '@angular/platform-browser';
import { HttpClientModule } from '@angular/common/http';
import {APP_INITIALIZER, NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {UIComponentsModule, USER_DATA_SERVICE} from '@ui-app/app.module';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {PhotoReviewService} from './core/services/data-access/photo-review.service';
import {HTTP_HEADER_INTERCEPTOR_PROVIDER} from '@ui-app/http-interceptors/http-headers.interceptor';
import {AppInitService} from '@ui-app/services/app-init.service';
import {LoaderBlackListToken} from '@ui-app/http-interceptors/http-loader.interceptor';
import {UserDataService} from './core/services/data-access/user-data.service';
import {
    API_CONFIG,
    DELETE_URL,
    ErrorBlackListToken,
    GET_ATTACHMENT_DOWNLOAD_URL,
    GET_ATTACHMENT_UPLOAD_URL,
    GET_DOWNLOAD_3RDPTY_TEMPLATE,
    GET_DOWNLOAD_CLE_EXT_TEMPLATE,
    GET_DOWNLOAD_CLE_TEMPLATE,
    GET_DOWNLOAD_MF_TEMPLATE,
    GET_DOWNLOAD_ND_TEMPLATE,
    GET_DOWNLOAD_URL,
    GET_UPLOAD_3RDPTY_TEMPLATE,
    GET_UPLOAD_CLE_TEMPLATE,
    GET_UPLOAD_MF_TEMPLATE,
    GET_UPLOAD_ND_TEMPLATE,
    GET_VISITOR_DOWNLOAD_URL,
    UPLOAD_URL,
    UPLOAD_VISITOR_URL
} from '@shared-models/common.models';
import {ApiConfig} from '@employee-configs/api.config';
import {SharedEmployeeModals} from './modals';
import {ResponseStatus} from '@employee-models/common.enums';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {PROFILE_PROVIDER} from '@employee-pages/employee-profile/services/profile.provider';
import {NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask} from 'ngx-mask';
import { ViewMatchedEmployeesComponent } from '@employee-pages/employee-search/view-matched-employees/view-matched-employees.component';
import { FindemployeeComponent } from '@employee-pages/employee-search/findemployee/findemployee.component';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';

const LoaderBlackList = [
    {
        method: ['POST'],
        url: ApiConfig.File.deleteFile.replace('{uId}', '')
    },
    {
        method: ['GET'],
        url: ApiConfig.Dictionary.getRejectedPhotoReasons
    },
    {
        method: ['GET'],
        url: ApiConfig.BadgeRequests.deliveryLocations
    },
    {
        method: ['POST'],
        url: ApiConfig.PhotoReview.getSitesForBulkChangeSite
    }
];

const errorBlackList = [
    {
        status: ResponseStatus.NotFound,
        url: ApiConfig.Users.userPhoto.replace('{userAlias}', '')
    },
];

@NgModule({
    declarations: [
        AppComponent,
        SharedEmployeeModals
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        HttpClientModule,
        AppRoutingModule,
        UIComponentsModule.forRoot(),
        ReactiveFormsModule,
        FormsModule,
        NgxMaskDirective,
        NgxMaskPipe,
        NgMultiSelectDropDownModule.forRoot()
    ],
    providers: [
        PhotoReviewService,
        AppInitService,
        provideEnvironmentNgxMask(),
        HTTP_HEADER_INTERCEPTOR_PROVIDER,
        {
            provide: APP_INITIALIZER,
            useFactory: (config: AppInitService) => () => config.getSettings('/'),
            deps: [AppInitService],
            multi: true
        },
        {
            provide: LoaderBlackListToken,
            useValue: LoaderBlackList
        },
        {
            provide: USER_DATA_SERVICE,
            useExisting: UserDataService
        },
        {
            provide: UPLOAD_URL,
            useValue: ApiConfig.File.getUploadUrl
        },
        {
            provide: UPLOAD_VISITOR_URL,
            useValue: ApiConfig.File.getVisitorUploadUrl
        },
        {
            provide: DELETE_URL,
            useValue: ApiConfig.File.deleteFile
        },
        {
            provide: GET_DOWNLOAD_URL,
            useValue: ApiConfig.File.getDownloadUrl
        },
        {
            provide: GET_VISITOR_DOWNLOAD_URL,
            useValue: ApiConfig.File.getVisitorDownloadUrl
        },
        {
            provide: GET_ATTACHMENT_DOWNLOAD_URL,
            useValue: ApiConfig.Attachment.getDownloadUrl
        },
        {
            provide: GET_ATTACHMENT_UPLOAD_URL,
            useValue: ApiConfig.Attachment.getUploadUrl
        },
        // HANDLE BULK UPLOAD
        {
            provide: GET_DOWNLOAD_ND_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.downloadNonDeloitteTemplate
        },
        {
            provide: GET_DOWNLOAD_MF_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.downloadMemberFirmTemplate
        },
        {
            provide: GET_DOWNLOAD_3RDPTY_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.downloadContractorTemplate
        },
        {
            provide: GET_DOWNLOAD_CLE_TEMPLATE,
            useValue: ApiConfig.BulkCleContractorUpload.downloadCleTemplate
        },
        {
            provide: GET_DOWNLOAD_CLE_EXT_TEMPLATE,
            useValue: ApiConfig.BulkCleContractorUpload.downloadCleExtTemplate
        },
        {
            provide: GET_UPLOAD_CLE_TEMPLATE,
            useValue: ApiConfig.BulkCleContractorUpload.uploadCleTemplate
        },
        {
            provide: GET_UPLOAD_ND_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.uploadNonDeloitteTemplate
        },
        {
            provide: GET_UPLOAD_MF_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.uploadMemberFirmTemplate
        },
        {
            provide: GET_UPLOAD_3RDPTY_TEMPLATE,
            useValue: ApiConfig.BulkVisitorUpload.uploadContractorTemplate
        },
        {
            provide: API_CONFIG,
            useValue: ApiConfig
        },
        {
            provide: ErrorBlackListToken,
            useValue: errorBlackList
        },
        PROFILE_PROVIDER,
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
